import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;
const AUTH_USER_KEY = 'fq_auth_user';
const AUTH_TOKEN_KEY = 'fq_auth_token';

/** Get stored Bearer token for Safari/iPhone fallback */
export const getAuthToken = () => localStorage.getItem(AUTH_TOKEN_KEY);

/** Build headers with Bearer token when available */
export const authHeaders = () => {
  const token = getAuthToken();
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

export const AuthProvider = ({ children }) => {
  const [user, setUserState] = useState(() => {
    try {
      const stored = localStorage.getItem(AUTH_USER_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  const setUser = useCallback((userData, token) => {
    setUserState(userData);
    if (userData) {
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(userData));
      if (token) localStorage.setItem(AUTH_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(AUTH_USER_KEY);
      localStorage.removeItem(AUTH_TOKEN_KEY);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    try {
      // The fetch interceptor in index.js adds Bearer token automatically,
      // but also send credentials for cookie-based auth as a secondary mechanism
      const res = await fetch(`${API_URL}/api/auth/me`, { credentials: 'include' });
      if (res.ok) {
        const userData = await res.json();
        setUserState(userData);
        localStorage.setItem(AUTH_USER_KEY, JSON.stringify(userData));
      } else if (res.status === 401) {
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        if (!token) {
          setUser(null);
        } else {
          // Token exists but server still rejected — check grace period
          const loginTs = parseInt(localStorage.getItem('fq_auth_ts') || '0');
          if (Date.now() - loginTs < 30000) {
            // Within 30s of login — keep user from localStorage (token propagation delay)
          } else {
            // Token truly invalid/expired — clear auth
            setUser(null);
          }
        }
      }
    } catch {
      // Network error — keep existing localStorage user
    } finally {
      setLoading(false);
    }
  }, [setUser]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Login failed');
    setUser(data.user, data.session_token);
    localStorage.setItem('fq_auth_ts', Date.now().toString());
    return data.user;
  };

  const register = async (name, email, password) => {
    const res = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name, email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Registration failed');
    setUser(data.user, data.session_token);
    localStorage.setItem('fq_auth_ts', Date.now().toString());
    return data.user;
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, { method: 'POST', credentials: 'include', headers: authHeaders() });
    } catch {}
    setUser(null);
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, loginWithGoogle, refetchUser: checkAuth, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};
